import React, { useState } from 'react';

const DetailedPLCBottleFlowchart = () => {
  const [highlightedStep, setHighlightedStep] = useState(null);
  const [selectedSection, setSelectedSection] = useState('all');

  const handleStepHover = (stepId) => {
    setHighlightedStep(stepId);
  };

  const handleStepLeave = () => {
    setHighlightedStep(null);
  };

  const ProcessBox = ({ id, x, y, width, height, text, type = 'process', section = 'general', ioPoints = [] }) => {
    const isHighlighted = highlightedStep === id;
    const isVisible = selectedSection === 'all' || selectedSection === section;
    
    const getColors = () => {
      if (!isVisible) return 'fill-gray-100 stroke-gray-300 opacity-30';
      
      switch (type) {
        case 'start':
          return isHighlighted ? 'fill-green-600 stroke-green-800' : 'fill-green-500 stroke-green-700';
        case 'decision':
          return isHighlighted ? 'fill-yellow-400 stroke-yellow-600' : 'fill-yellow-300 stroke-yellow-500';
        case 'error':
          return isHighlighted ? 'fill-red-600 stroke-red-800' : 'fill-red-500 stroke-red-700';
        case 'end':
          return isHighlighted ? 'fill-blue-600 stroke-blue-800' : 'fill-blue-500 stroke-blue-700';
        case 'sensor':
          return isHighlighted ? 'fill-purple-400 stroke-purple-600' : 'fill-purple-300 stroke-purple-500';
        case 'motor':
          return isHighlighted ? 'fill-orange-400 stroke-orange-600' : 'fill-orange-300 stroke-orange-500';
        case 'hmi':
          return isHighlighted ? 'fill-cyan-400 stroke-cyan-600' : 'fill-cyan-300 stroke-cyan-500';
        default:
          return isHighlighted ? 'fill-gray-300 stroke-gray-500' : 'fill-gray-200 stroke-gray-400';
      }
    };

    const shape = type === 'decision' ? (
      <polygon
        points={`${x + width/2},${y} ${x + width},${y + height/2} ${x + width/2},${y + height} ${x},${y + height/2}`}
        className={getColors()}
        strokeWidth="2"
      />
    ) : (
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx={type === 'start' || type === 'end' ? 20 : 5}
        className={getColors()}
        strokeWidth="2"
      />
    );

    return (
      <g 
        onMouseEnter={() => handleStepHover(id)}
        onMouseLeave={handleStepLeave}
        className="cursor-pointer transition-all duration-200"
      >
        {shape}
        <text
          x={x + width/2}
          y={y + height/2 - 10}
          textAnchor="middle"
          dominantBaseline="middle"
          className={`text-xs font-medium pointer-events-none ${isVisible ? 'fill-gray-800' : 'fill-gray-400'}`}
        >
          {text.split('\n').map((line, i) => (
            <tspan key={i} x={x + width/2} dy={i === 0 ? 0 : 12}>
              {line}
            </tspan>
          ))}
        </text>
        {ioPoints.length > 0 && isVisible && (
          <text
            x={x + width/2}
            y={y + height - 8}
            textAnchor="middle"
            className="text-xs fill-gray-600 pointer-events-none"
          >
            {ioPoints.join(', ')}
          </text>
        )}
      </g>
    );
  };

  const Arrow = ({ x1, y1, x2, y2, label = '', condition = '' }) => {
    const isVisible = selectedSection === 'all';
    
    return (
      <g className={isVisible ? '' : 'opacity-30'}>
        <line
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke="#374151"
          strokeWidth="2"
          markerEnd="url(#arrowhead)"
        />
        {label && (
          <text
            x={(x1 + x2) / 2}
            y={(y1 + y2) / 2 - 5}
            textAnchor="middle"
            className="text-xs font-medium fill-gray-700"
          >
            {label}
          </text>
        )}
        {condition && (
          <text
            x={(x1 + x2) / 2}
            y={(y1 + y2) / 2 + 15}
            textAnchor="middle"
            className="text-xs fill-blue-600"
          >
            {condition}
          </text>
        )}
      </g>
    );
  };

  const sections = [
    { id: 'all', name: 'All Systems', color: 'gray' },
    { id: 'safety', name: 'Safety Systems', color: 'red' },
    { id: 'material', name: 'Material Handling', color: 'green' },
    { id: 'molding', name: 'Molding Process', color: 'blue' },
    { id: 'quality', name: 'Quality Control', color: 'purple' },
    { id: 'filling', name: 'Filling & Capping', color: 'orange' },
    { id: 'hmi', name: 'HMI & Data', color: 'cyan' }
  ];

  return (
    <div className="w-full max-w-7xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-3xl font-bold text-center mb-6 text-gray-800">
        Detailed PLC Flowchart - Automated Bottle Production Line
      </h2>
      
      {/* Section Filter */}
      <div className="mb-6 flex flex-wrap gap-2 justify-center">
        {sections.map(section => (
          <button
            key={section.id}
            onClick={() => setSelectedSection(section.id)}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
              selectedSection === section.id
                ? `bg-${section.color}-500 text-white shadow-md`
                : `bg-${section.color}-100 text-${section.color}-700 hover:bg-${section.color}-200`
            }`}
          >
            {section.name}
          </button>
        ))}
      </div>
      
      <div className="overflow-x-auto">
        <svg width="1400" height="2200" className="border border-gray-300 rounded">
          {/* Arrow marker definition */}
          <defs>
            <marker
              id="arrowhead"
              markerWidth="10"
              markerHeight="7"
              refX="9"
              refY="3.5"
              orient="auto"
            >
              <polygon points="0 0, 10 3.5, 0 7" fill="#374151" />
            </marker>
          </defs>

          {/* SYSTEM INITIALIZATION SECTION */}
          <ProcessBox 
            id="start" 
            x={600} y={20} width={120} height={50} 
            text="System Power ON\nPLC Boot Sequence" 
            type="start" 
            section="safety"
            ioPoints={['I:0/0']}
          />
          
          <ProcessBox 
            id="init-io" 
            x={550} y={90} width={220} height={50} 
            text="Initialize I/O Modules\nEthernet/IP Communication\nDiagnostic Self-Test" 
            type="process" 
            section="safety"
            ioPoints={['Rack0', 'Rack1', 'Rack2']}
          />
          
          <ProcessBox 
            id="load-recipe" 
            x={550} y={160} width={220} height={50} 
            text="Load Production Recipe\nSet Process Parameters\nInitialize Timers/Counters" 
            type="hmi" 
            section="hmi"
            ioPoints={['HMI_Recipe', 'T4:0-T4:10']}
          />

          {/* SAFETY SYSTEM CHECKS */}
          <ProcessBox 
            id="safety-scan" 
            x={550} y={230} width={220} height={50} 
            text="Scan Safety Circuits\nE-Stop Status Check\nLight Curtain Test" 
            type="process" 
            section="safety"
            ioPoints={['I:1/0-7', 'Safety_OK']}
          />
          
          <ProcessBox 
            id="guard-check" 
            x={550} y={300} width={220} height={50} 
            text="Guard Door Position\nSafety Interlock Status\nPressure Relief Check" 
            type="sensor" 
            section="safety"
            ioPoints={['I:1/8-15', 'Guard_Closed']}
          />
          
          <ProcessBox 
            id="safety-decision" 
            x={600} y={370} width={120} height={70} 
            text="All Safety\nSystems\nOK?" 
            type="decision" 
            section="safety"
          />
          
          <ProcessBox 
            id="safety-alarm" 
            x={50} y={390} width={200} height={50} 
            text="Safety Alarm Activated\nHMI Error Display\nMaintenance Required" 
            type="error" 
            section="safety"
            ioPoints={['O:2/0', 'Alarm_Horn']}
          />

          {/* MATERIAL HANDLING SECTION */}
          <ProcessBox 
            id="material-check" 
            x={550} y={460} width={220} height={50} 
            text="Check Raw Material Level\nPreform Hopper Status\nResin Feed System" 
            type="sensor" 
            section="material"
            ioPoints={['AI:3/0-3', 'Level_Switch']}
          />
          
          <ProcessBox 
            id="material-decision" 
            x={600} y={530} width={120} height={70} 
            text="Material\nLevel\nAdequate?" 
            type="decision" 
            section="material"
          />
          
          <ProcessBox 
            id="material-alarm" 
            x={900} y={545} width={180} height={50} 
            text="Low Material Alarm\nFeed System Activation\nOperator Notification" 
            type="error" 
            section="material"
            ioPoints={['O:2/5', 'Feed_Motor']}
          />
          
          <ProcessBox 
            id="conveyor-start" 
            x={550} y={620} width={220} height={50} 
            text="Start Preform Conveyor\nVibrating Feeder ON\nOrientation Check" 
            type="motor" 
            section="material"
            ioPoints={['O:3/0', 'M1_Run', 'VFD1']}
          />

          {/* HEATING SECTION */}
          <ProcessBox 
            id="heater-zones" 
            x={550} y={690} width={220} height={50} 
            text="Activate Heating Zones\n4-Zone Temperature Control\nPID Loop Initialization" 
            type="process" 
            section="molding"
            ioPoints={['AO:4/0-3', 'PID_Temp']}
          />
          
          <ProcessBox 
            id="temp-monitor" 
            x={550} y={760} width={220} height={50} 
            text="Temperature Monitoring\nThermocouple Readings\nZone Balance Check" 
            type="sensor" 
            section="molding"
            ioPoints={['AI:4/0-3', 'TC1-TC4']}
          />
          
          <ProcessBox 
            id="temp-decision" 
            x={600} y={830} width={120} height={70} 
            text="Target Temp\n±5°C\nReached?" 
            type="decision" 
            section="molding"
          />
          
          <ProcessBox 
            id="temp-wait" 
            x={350} y={845} width={180} height={50} 
            text="Heat Soak Timer\nTemperature Ramp Control\nEnergy Optimization" 
            type="process" 
            section="molding"
            ioPoints={['T4:5', 'Heat_Ramp']}
          />

          {/* MOLDING PROCESS */}
          <ProcessBox 
            id="mold-position" 
            x={550} y={920} width={220} height={50} 
            text="Position Mold Halves\nHydraulic Clamp Close\nClamp Force Monitor" 
            type="motor" 
            section="molding"
            ioPoints={['O:5/0-2', 'Hyd_Clamp']}
          />
          
          <ProcessBox 
            id="injection-prep" 
            x={550} y={990} width={220} height={50} 
            text="Injection Unit Forward\nScrew Position Home\nShot Size Verification" 
            type="process" 
            section="molding"
            ioPoints={['O:5/3-5', 'Screw_Pos']}
          />
          
          <ProcessBox 
            id="injection-cycle" 
            x={550} y={1060} width={220} height={50} 
            text="Injection Cycle Start\nPressure Profile Control\nFlow Rate Monitoring" 
            type="process" 
            section="molding"
            ioPoints={['Injection_Cycle', 'AI:5/0']}
          />
          
          <ProcessBox 
            id="cooling-timer" 
            x={550} y={1130} width={220} height={50} 
            text="Cooling Timer Start\nMold Temperature Control\nCycle Time Optimization" 
            type="process" 
            section="molding"
            ioPoints={['T4:10', 'Cool_Valves']}
          />
          
          <ProcessBox 
            id="cooling-decision" 
            x={600} y={1200} width={120} height={70} 
            text="Cooling\nTime\nComplete?" 
            type="decision" 
            section="molding"
          />

          {/* PART EXTRACTION */}
          <ProcessBox 
            id="mold-open" 
            x={550} y={1290} width={220} height={50} 
            text="Mold Open Sequence\nEjector Pins Activate\nPart Removal System" 
            type="motor" 
            section="molding"
            ioPoints={['O:6/0-2', 'Ejector']}
          />
          
          <ProcessBox 
            id="part-transfer" 
            x={550} y={1360} width={220} height={50} 
            text="Robot Part Transfer\n6-Axis Motion Control\nGripper Operation" 
            type="motor" 
            section="material"
            ioPoints={['Robot_Control', 'Gripper']}
          />

          {/* QUALITY INSPECTION */}
          <ProcessBox 
            id="vision-inspect" 
            x={550} y={1430} width={220} height={50} 
            text="Vision System Inspection\nDimensional Measurement\nSurface Defect Detection" 
            type="sensor" 
            section="quality"
            ioPoints={['Vision_Sys', 'Laser_Measure']}
          />
          
          <ProcessBox 
            id="weight-check" 
            x={550} y={1500} width={220} height={50} 
            text="Weight Verification\nLoad Cell Reading\nStatistical Analysis" 
            type="sensor" 
            section="quality"
            ioPoints={['AI:7/0', 'Load_Cell']}
          />
          
          <ProcessBox 
            id="quality-decision" 
            x={600} y={1570} width={120} height={70} 
            text="Quality\nSpecs\nMet?" 
            type="decision" 
            section="quality"
          />
          
          <ProcessBox 
            id="reject-handling" 
            x={900} y={1585} width={200} height={50} 
            text="Pneumatic Reject System\nSort to Regrind Bin\nDefect Counter Update" 
            type="motor" 
            section="quality"
            ioPoints={['O:8/0', 'Reject_Air', 'C5:0']}
          />

          {/* FILLING SECTION */}
          <ProcessBox 
            id="conveyor-index" 
            x={550} y={1660} width={220} height={50} 
            text="Conveyor Index System\nBottle Positioning\nStar Wheel Control" 
            type="motor" 
            section="filling"
            ioPoints={['O:9/0', 'Star_Motor', 'Encoder']}
          />
          
          <ProcessBox 
            id="fill-head-position" 
            x={550} y={1730} width={220} height={50} 
            text="Fill Head Positioning\nNozzle Height Adjust\nFill Volume Preset" 
            type="motor" 
            section="filling"
            ioPoints={['Servo_Fill', 'Fill_Volume']}
          />
          
          <ProcessBox 
            id="filling-process" 
            x={550} y={1800} width={220} height={50} 
            text="Liquid Filling Process\nFlow Meter Monitoring\nLevel Sensor Check" 
            type="process" 
            section="filling"
            ioPoints={['Fill_Valve', 'AI:10/0']}
          />
          
          <ProcessBox 
            id="fill-decision" 
            x={600} y={1870} width={120} height={70} 
            text="Fill Level\n±2ml\nCorrect?" 
            type="decision" 
            section="filling"
          />

          {/* CAPPING SECTION */}
          <ProcessBox 
            id="cap-placement" 
            x={550} y={1960} width={220} height={50} 
            text="Cap Placement System\nVibrating Cap Feeder\nOrientation Verification" 
            type="motor" 
            section="filling"
            ioPoints={['Cap_Feed', 'O:11/0-2']}
          />
          
          <ProcessBox 
            id="torque-control" 
            x={550} y={2030} width={220} height={50} 
            text="Torque Capping System\nServo Motor Control\nTorque Feedback" 
            type="motor" 
            section="filling"
            ioPoints={['Servo_Cap', 'Torque_FB']}
          />
          
          <ProcessBox 
            id="final-inspection" 
            x={550} y={2100} width={220} height={50} 
            text="Final Quality Check\nCap Presence Detection\nLeak Test Optional" 
            type="sensor" 
            section="quality"
            ioPoints={['Cap_Sensor', 'Leak_Test']}
          />

          {/* OUTPUT AND DATA LOGGING */}
          <ProcessBox 
            id="data-logging" 
            x={300} y={2100} width={200} height={50} 
            text="Production Data Log\nSPC Database Update\nBatch Record Creation" 
            type="hmi" 
            section="hmi"
            ioPoints={['SQL_Log', 'Batch_ID']}
          />
          
          <ProcessBox 
            id="output-conveyor" 
            x={550} y={2170} width={220} height={50} 
            text="Output Conveyor Start\nCase Packing Ready\nProduction Counter" 
            type="end" 
            section="material"
            ioPoints={['O:12/0', 'C5:1', 'Pack_Ready']}
          />

          {/* Main Process Flow Arrows */}
          <Arrow x1={660} y1={70} x2={660} y2={90} />
          <Arrow x1={660} y1={140} x2={660} y2={160} />
          <Arrow x1={660} y1={210} x2={660} y2={230} />
          <Arrow x1={660} y1={280} x2={660} y2={300} />
          <Arrow x1={660} y1={350} x2={660} y2={370} />
          
          {/* Safety No Path */}
          <Arrow x1={600} y1={405} x2={250} y2={405} label="No" condition="Safety Fault" />
          
          {/* Safety Yes Path */}
          <Arrow x1={660} y1={440} x2={660} y2={460} label="Yes" />
          
          <Arrow x1={660} y1={510} x2={660} y2={530} />
          
          {/* Material No Path */}
          <Arrow x1={720} y1={565} x2={900} y2={565} label="No" condition="Low Level" />
          
          {/* Material Yes Path */}
          <Arrow x1={660} y1={600} x2={660} y2={620} label="Yes" />
          
          <Arrow x1={660} y1={670} x2={660} y2={690} />
          <Arrow x1={660} y1={740} x2={660} y2={760} />
          <Arrow x1={660} y1={810} x2={660} y2={830} />
          
          {/* Temperature Loop */}
          <Arrow x1={600} y1={865} x2={440} y2={865} label="No" condition="Heat More" />
          <Arrow x1={440} y1={845} x2={440} y2={780} />
          <Arrow x1={440} y1={780} x2={550} y2={780} />
          
          {/* Temperature Yes */}
          <Arrow x1={660} y1={900} x2={660} y2={920} label="Yes" />
          
          <Arrow x1={660} y1={970} x2={660} y2={990} />
          <Arrow x1={660} y1={1040} x2={660} y2={1060} />
          <Arrow x1={660} y1={1110} x2={660} y2={1130} />
          <Arrow x1={660} y1={1180} x2={660} y2={1200} />
          
          {/* Cooling Loop */}
          <Arrow x1={600} y1={1235} x2={520} y2={1235} label="No" condition="Continue Cool" />
          <Arrow x1={520} y1={1235} x2={520} y2={1150} />
          <Arrow x1={520} y1={1150} x2={550} y2={1150} />
          
          {/* Cooling Yes */}
          <Arrow x1={660} y1={1270} x2={660} y2={1290} label="Yes" />
          
          <Arrow x1={660} y1={1340} x2={660} y2={1360} />
          <Arrow x1={660} y1={1410} x2={660} y2={1430} />
          <Arrow x1={660} y1={1480} x2={660} y2={1500} />
          <Arrow x1={660} y1={1550} x2={660} y2={1570} />
          
          {/* Quality Reject Path */}
          <Arrow x1={720} y1={1605} x2={900} y2={1605} label="No" condition="Reject" />
          
          {/* Quality Pass */}
          <Arrow x1={660} y1={1640} x2={660} y2={1660} label="Yes" />
          
          <Arrow x1={660} y1={1710} x2={660} y2={1730} />
          <Arrow x1={660} y1={1780} x2={660} y2={1800} />
          <Arrow x1={660} y1={1850} x2={660} y2={1870} />
          
          {/* Fill Level No */}
          <Arrow x1={720} y1={1905} x2={1000} y2={1905} label="No" condition="Refill/Reject" />
          <Arrow x1={1000} y1={1905} x2={1000} y2={1605} />
          
          {/* Fill Level Yes */}
          <Arrow x1={660} y1={1940} x2={660} y2={1960} label="Yes" />
          
          <Arrow x1={660} y1={2010} x2={660} y2={2030} />
          <Arrow x1={660} y1={2080} x2={660} y2={2100} />
          
          {/* Data Logging */}
          <Arrow x1={550} y1={2125} x2={500} y2={2125} />
          
          <Arrow x1={660} y1={2150} x2={660} y2={2170} />
          
          {/* Continuous Production Loop */}
          <Arrow x1={770} y1={2195} x2={1200} y2={2195} />
          <Arrow x1={1200} y1={2195} x2={1200} y2={45} />
          <Arrow x1={1200} y1={45} x2={720} y2={45} />

          {/* Recycle Paths */}
          <Arrow x1={900} y1={1605} x2={100} y2={1605} condition="Regrind" />
          <Arrow x1={100} y1={1605} x2={100} y2={640} />
          <Arrow x1={100} y1={640} x2={550} y2={640} />
        </svg>
      </div>
      
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Process Legend */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-800">Process Types</h3>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="p-2 bg-green-50 rounded border-l-4 border-green-500">
              <div className="font-semibold text-green-800">Start/End</div>
              <div className="text-green-700">System states</div>
            </div>
            <div className="p-2 bg-gray-50 rounded border-l-4 border-gray-500">
              <div className="font-semibold text-gray-800">Process</div>
              <div className="text-gray-700">Standard operations</div>
            </div>
            <div className="p-2 bg-yellow-50 rounded border-l-4 border-yellow-500">
              <div className="font-semibold text-yellow-800">Decision</div>
              <div className="text-yellow-700">Logic branches</div>
            </div>
            <div className="p-2 bg-purple-50 rounded border-l-4 border-purple-500">
              <div className="font-semibold text-purple-800">Sensor</div>
              <div className="text-purple-700">Input monitoring</div>
            </div>
            <div className="p-2 bg-orange-50 rounded border-l-4 border-orange-500">
              <div className="font-semibold text-orange-800">Motor</div>
              <div className="text-orange-700">Motion control</div>
            </div>
            <div className="p-2 bg-cyan-50 rounded border-l-4 border-cyan-500">
              <div className="font-semibold text-cyan-800">HMI</div>
              <div className="text-cyan-700">Interface/Data</div>
            </div>
          </div>
        </div>

        {/* Technical Specifications */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-800">Technical Specifications</h3>
          <div className="grid grid-cols-1 gap-3 text-sm">
            <div className="p-3 bg-blue-50 rounded">
              <div className="font-semibold text-blue-800">Control System</div>
              <div className="text-blue-700">Allen-Bradley ControlLogix PLC, Ethernet/IP Network, Distributed I/O</div>
            </div>
            <div className="p-3 bg-green-50 rounded">
              <div className="font-semibold text-green-800">Safety Systems</div>
              <div className="text-green-700">Category 3 Safety, Emergency Stop Circuits, Light Curtains, Guard Monitoring</div>
            </div>
            <div className="p-3 bg-purple-50 rounded">
              <div className="font-semibold text-purple-800">Quality Control</div>
              <div className="text-purple-700">Vision Inspection, Statistical Process Control, Dimensional Measurement</div>
            </div>
            <div className="p-3 bg-orange-50 rounded">
              <div className="font-semibold text-orange-800">Process Control</div>
              <div className="text-orange-700">PID Temperature Control, Servo Motion, Hydraulic Systems, Pneumatic Actuators</div>
            </div>
          </div>
        </div>
      </div>

      {/* I/O Summary Table */}
      <div className="mt-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">I/O Point Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="p-4 bg-gray-50 rounded">
            <h4 className="font-semibold text-gray-800 mb-2">Digital Inputs (96 points)</h4>
            <ul className="space-y-1 text-gray-700">
              <li>• Safety circuits (I:1/0-15)</li>
              <li>• Limit switches (I:2/0-31)</li>
              <li>• Proximity sensors (I:3/0-31)</li>
              <li>• Push buttons/HMI (I:4/0-15)</li>
            </ul>
          </div>
          <div className="p-4 bg-gray-50 rounded">
            <h4 className="font-semibold text-gray-800 mb-2">Digital Outputs (64 points)</h4>
            <ul className="space-y-1 text-gray-700">
              <li>• Motor starters (O:1/0-15)</li>
              <li>• Solenoid valves (O:2/0-31)</li>
              <li>• Indicator lights (O:3/0-15)</li>
            </ul>
          </div>
          <div className="p-4 bg-gray-50 rounded">
            <h4 className="font-semibold text-gray-800 mb-2">Analog I/O (32 points)</h4>
            <ul className="space-y-1 text-gray-700">
              <li>• Temperature inputs (AI:1-8)</li>
              <li>• Pressure transmitters (AI:9-16)</li>
              <li>• Flow meters (AI:17-20)</li>
              <li>• Control outputs (AO:1-12)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="mt-6 p-4 bg-blue-50 rounded">
        <h4 className="font-semibold text-blue-800 mb-2">Performance Metrics & Features:</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-blue-700 text-sm">
          <ul className="space-y-1">
            <li>• Cycle time: 12-15 seconds per bottle</li>
            <li>• Production rate: 240-300 bottles/hour</li>
            <li>• Quality reject rate: &lt;2%</li>
            <li>• Overall Equipment Efficiency (OEE): &gt;85%</li>
          </ul>
          <ul className="space-y-1">
            <li>• Real-time data logging and trending</li>
            <li>• Predictive maintenance algorithms</li>
            <li>• Remote monitoring capabilities</li>
            <li>• Recipe-based production flexibility</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DetailedPLCBottleFlowchart;